import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { auth } from '@/lib/api';
import { Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

function isMobileBrowser() {
  return /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const platform = searchParams.get('platform');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showReturnToApp, setShowReturnToApp] = useState(false);

  // A missing/empty token can never succeed (and posting token: null returns
  // a 422 whose array-shaped detail would crash the error toast), so render
  // a clear invalid-link state instead of a form that can only fail.
  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <Link to="/" className="flex items-center gap-2 w-fit">
              <BrandLogo variant="black" size="lg" />
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <Card className="w-full max-w-md border-slate-200 rounded-xl shadow-sm">
            <CardHeader className="text-left">
              <CardTitle className="text-2xl font-bold font-['Manrope'] text-slate-900">
                Invalid reset link
              </CardTitle>
              <CardDescription className="text-slate-600">
                This password reset link is missing or incomplete. Please request a new one.
              </CardDescription>
            </CardHeader>
            <CardContent data-testid="reset-password-invalid-token">
              <Link
                to="/forgot-password"
                className="font-medium text-emerald-600 hover:text-emerald-700"
                data-testid="reset-password-invalid-token-request-new-link"
              >
                Request a new reset link
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await auth.resetPassword(token, newPassword);

      if (platform === 'mobile' && isMobileBrowser()) {
        setShowReturnToApp(true);
      } else {
        toast.success('Password reset successfully!');
        navigate('/login');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <BrandLogo variant="black" size="lg" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <Card className="w-full max-w-md border-slate-200 rounded-xl shadow-sm">
          {showReturnToApp ? (
            <>
              <CardHeader className="text-left">
                <CardTitle className="text-2xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-2">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  Password reset
                </CardTitle>
                <CardDescription className="text-slate-600">
                  Your password has been reset successfully.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4" data-testid="reset-password-return-to-app">
                <Button
                  asChild
                  className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-md"
                  data-testid="reset-password-return-to-app-btn"
                >
                  <a href="frontend://login">Return to app</a>
                </Button>
                <p className="text-center text-sm text-slate-600">
                  <Link
                    to="/login"
                    className="font-medium text-emerald-600 hover:text-emerald-700"
                    data-testid="reset-password-continue-to-login-link"
                  >
                    Continue to login
                  </Link>
                </p>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="text-left">
                <CardTitle className="text-2xl font-bold font-['Manrope'] text-slate-900">
                  Set a new password
                </CardTitle>
                <CardDescription className="text-slate-600">
                  Enter and confirm your new password
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-slate-700">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        name="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                        disabled={isLoading}
                        data-testid="reset-password-new-input"
                        className="rounded-md pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        data-testid="reset-password-new-toggle"
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-4 w-4 text-slate-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-400" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">At least 6 characters</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-slate-700">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        data-testid="reset-password-confirm-input"
                        className="rounded-md pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        data-testid="reset-password-confirm-toggle"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-slate-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-400" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-slate-900 hover:bg-slate-800 rounded-md"
                    disabled={isLoading}
                    data-testid="reset-password-submit-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      'Reset password'
                    )}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}
